<script lang="ts">
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';
    import { fade } from 'svelte/transition';

    import { TacticalPlanApi, type TacticalPlanResponseType } from './TacticalPlanApi';
    // onUpdateOne jest wywoływane zarówno przez WeekDaysShortSummary (z odpowiedziami PATCH)
    // jak i przez removeOne (bez argumentów) — dlatego parametr jest opcjonalny
    import WeekDaysShortSummary from '../../shared/WeekDaysShortSummary.svelte';
    import MenuContext from '../MenuDropdown/MenuContext.svelte';
    import { debug } from '../../../stores/store';
    import { TacticalPlan } from './TacticalPlan';
    import { api } from '../../../stores/api';
    import { openModal } from 'svelte-modals';
    import ModalRemoveSomething from '../Modals/ModalRemoveSomething.svelte';
    import IconCheck from '../../shared/Icons/IconCheck.svelte';
    import { week } from '../../utilities/mockWeekSchedule';
    import { WeekSchedule } from '../../utilities/WeekSchedule';
    import ModalInfo from '../../shared/Modals/ModalInfo.svelte';
    import { getWeekdayShort } from '../../utilities/Weekdays';

    export let tacticalPlan: TacticalPlan;
    export let allTacticalPlans: TacticalPlanResponseType[] = [];
    export let showHeader: boolean = false;
    export let error: string = '';
    export let onUpdateOne: (updatedPlans?: TacticalPlanResponseType[]) => void;
    export let onCloneTacticalPlan: (tacticalPlan: TacticalPlanResponseType) => void;
    export let willBeNextItem: boolean = false;
    export let disableChanging: boolean = false;

    export let clickedTacticalPlanId: number = -1;

    // let tp: TacticalPlan = new TacticalPlan(tacticalPlan);

    function removeOneWithConfirm() {
        if (tacticalPlan.getWeekSchedule() !== 0 && !$debug) {
            openModal(ModalInfo, {
                text: $_('You cannot remove tactical plan which has a day of the week set.'),
            });
        } else {
            openModal(ModalRemoveSomething, {
                text: $_('Are you sure you want to remove this tactical plan?'),
                name: tacticalPlan?.getName(),
                onClick: () => {
                    removeOne();
                },
            });
        }
    }

    function removeOne() {
        let id = tacticalPlan?.getId();
        error = '';
        if (tacticalPlan && id) {
            TacticalPlanApi.removeOne(get(api), id)
                .then(() => {
                    // tacticalPlan = null;
                    onUpdateOne();
                })
                .catch(e => {
                    error = $_('tactical_plan_delete_error'); // TODO: Co ma zrobić user?
                });
        } else {
            alert($_('tactical_plan_delete_failed'));
        }
    }

    function onClone() {
        let tacticalPlanData = tacticalPlan._get();
        if (tacticalPlanData === null) throw new Error('tacticalPlan is null');
        if (tacticalPlanData) {
            onCloneTacticalPlan(tacticalPlanData);
        } else {
            alert($_('tactical_plan_clone_failed'));
        }
    }
</script>

{#if showHeader}
    <div class="tactical-element tactical-element-header">
        <div class="filename">{$_('Name')}</div>
        <div class="vehicle-status">{$_('count_of_assigned_vehicles')}</div>
        <div class="week-schedule" style="justify-content: center;">
            <div class="title">{$_('used_by_default_on_day')}</div>
            <div class="days">
                {#each week as day, index}
                    <div style="text-transform: capitalize; {day?.style}">
                        {$_(day.translationShort).substring(0, 2)}
                    </div>
                {/each}
            </div>
        </div>
        <div class="remove">{$_('Action')}</div>
    </div>
{/if}

{#if tacticalPlan}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="tactical-element tactical-element-data"
        on:click
        in:fade={{ duration: 500, delay: 200 }}
    >
        <div class="filename" title={tacticalPlan.getName()}>
            {#if $debug}
                ctp={clickedTacticalPlanId}
                #{tacticalPlan.getId()} - {tacticalPlan.getName()} / {tacticalPlan.getWeekSchedule()}
            {:else}
                <div
                    style="        white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 0 1 auto;"
                >
                    {tacticalPlan.getName()}
                </div>
            {/if}
        </div>
        <div class="vehicle-status">
            {#if tacticalPlan.getCountVehicle() > 0}
                <div out:fade={{ duration: 500, delay: 500 }}>
                    {tacticalPlan.getCountVehicle()}
                </div>
            {:else}
                -
            {/if}
        </div>
        <div class="week-schedule" on:click|stopPropagation={() => {}}>
            <WeekDaysShortSummary
                bind:tacticalPlan
                bind:clickedTacticalPlanId
                onChange={onUpdateOne}
                {willBeNextItem}
                bind:disabled={disableChanging}
                {allTacticalPlans}
            />
        </div>
        <div class="remove" on:click|stopPropagation={() => {}}>
            <MenuContext onRemove={removeOneWithConfirm} {onClone} />
        </div>
    </div>
{/if}

<style>
    .tactical-element {
        position: relative;
        box-sizing: border-box;
        display: grid;
        grid-template-columns: minmax(100px, 1fr) minmax(30px, 1fr) 230px 30px;
        grid-template-rows: var(--medium-row-height);
        gap: 0 0;
        /*border-bottom: 1px solid #eeeeee; !* #dedede; *!*/
        grid-template-areas: 'filename vehicle-status week-schedule remove';
    }

    .tactical-element-header {
        border-bottom: var(--list-element-header-border-bottom);
        padding-bottom: 5px;
        line-height: 18px;
        font-size: var(--list-element-header-font-size);
        color: var(--list-element-header-color);
    }

    .tactical-element-header .week-schedule {
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-content: flex-end;
    }

    .tactical-element-header .week-schedule .title {
        color: var(--list-element-header-color-inactive);
    }

    .tactical-element-header .week-schedule .days {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        column-gap: 7px;
        align-items: center;
        width: 191px;
    }

    .tactical-element-header .filename {
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        color: var(--list-element-header-color-inactive);
    }

    .filename {
        padding: 0 0 0 5px;
        display: flex;
    }

    .tactical-element-header .vehicle-status,
    .tactical-element-header .remove {
        display: flex;
        justify-content: center;
        align-items: flex-end;
        color: var(--list-element-header-color-inactive);
        text-align: center;
    }

    .tactical-element-data {
        border-bottom: var(--list-element-data-border-bottom);
        font-size: var(--list-element-data-font-size);
        /*padding-bottom: 5px;*/
        /*line-height: 18px;*/
    }

    .tactical-element-data > div {
        display: flex;
        flex-direction: row;
        align-items: center;
        /*justify-content: center;*/
        /*text-overflow: ellipsis;*/
        /*white-space: nowrap;*/
        /*overflow: hidden;*/
    }

    .tactical-element-data:hover {
        background-color: var(--main-selected-background);
        cursor: pointer;
    }

    .week-schedule {
        grid-area: week-schedule;
        justify-content: center;
        padding: 0 5px;
    }

    .filename {
        grid-area: filename;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        /*cursor: pointer;*/
    }

    .vehicle-status {
        grid-area: vehicle-status;
        justify-content: center;
    }

    .remove {
        grid-area: remove;
        justify-content: center;
    }
</style>
